import { configureStore } from "@reduxjs/toolkit";
import { batchDispatchMiddleware } from "redux-batched-actions";
import createSagaMiddleware from "redux-saga";
import { createLogger } from "redux-logger";

import { RequireRefreshError } from "~/notifications";
import { Ai360API, LegacyApi } from "@ai360/core";
import { rootReducer } from "./root-reducer";
import { rootSaga } from "./root-saga";

const middlewares = [];
const sagaMiddleware = createSagaMiddleware();
middlewares.push(sagaMiddleware);
middlewares.push(batchDispatchMiddleware);
middlewares.push(Ai360API.middleware);
middlewares.push(LegacyApi.middleware);

declare global {
    interface Window {
        process_env: any;
        __REDUX_DEVTOOLS_EXTENSION_COMPOSE__: any;
        store: any;
    }
}

if (!window.process_env.NO_LOGGER && window.process_env.NODE_ENV === "development") {
    const logger = createLogger({
        duration: true,
        timestamp: true,
    });
    middlewares.push(logger);
}

const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            immutableCheck: false, // Disable immutable checks
            serializableCheck: false, // Optionally disable serialization checks
        }).concat(middlewares),
    devTools: !import.meta.env.PROD
        ? {
              maxAge: 100,
              trace: true,
              traceLimit: 50,
              autoPause: true,
              shouldRecordChanges: false,
          }
        : false, // Automatically use Redux DevTools if not in production
});

// Expose the store on window for debugging — development only. Gate on the same
// explicit NODE_ENV === "development" signal used for redux-logger above, not the
// broad `!import.meta.env.PROD` (which is also true for test/preview builds).
if (window.process_env.NODE_ENV === "development") {
    window.store = store;
}

export default store;

const rootTask = sagaMiddleware.run(rootSaga);
rootTask.done.catch((err) => {
    // one of the sagas has an uncaught error, so we should probably reload the app
    throw new RequireRefreshError(err.toString());
});
